'use client'

import type { LucideIcon } from 'lucide-react'
import { ArrowLeft, Bot, Code2, Gauge, Monitor, Network, Presentation, Search, Users, Wrench } from 'lucide-react'
import Link from 'next/link'
import { useEffect } from 'react'
import { ServiceCard } from '@/features/service/view/ServiceCard'
import { ServiceDreadPanel } from '@/features/service/view/ServiceDreadPanel'
import { ServiceEngagementStrip } from '@/features/service/view/ServiceEngagementStrip'
import { routePaths } from '@/shared/config/site'
import { gtmEvent } from '@/shared/lib/googleTag'
import { Button } from '@/shared/ui/Button'

const serviceIcons: LucideIcon[] = [Code2, Monitor, Users, Wrench, Presentation, Bot, Network, Gauge, Users]

export function ServicePage() {
  useEffect(() => {
    document.title = 'Services | Vitaliy'
  }, [])

  useEffect(() => {
    gtmEvent('service_page_view', { page_path: '/service' })
  }, [])

  return (
    <div className="page-shell px-5 py-24 md:px-8 lg:px-10">
      <main className="mx-auto max-w-6xl" id={'content'}>
        <div className="mb-10 flex flex-wrap items-center gap-3">
          <Button asChild size="sm" variant="ghost">
            <Link href={routePaths.home}>
              <ArrowLeft className="h-4 w-4" />
              {'Back Home'}
            </Link>
          </Button>
        </div>

        <header className="relative overflow-hidden border-y border-white/10 py-12 md:py-16">
          <div className="hero-grid-plane pointer-events-none absolute inset-0 opacity-20" />
          <div className="absolute inset-x-0 bottom-0 h-px bg-fault-red/50" />
          <div className="relative grid gap-10 lg:grid-cols-[minmax(0,1fr)_24rem] lg:items-center">
            <div>
              <h1 className="display-title text-6xl leading-none md:text-7xl">{'Services'}</h1>
              <p className="mt-7 max-w-4xl text-2xl font-semibold leading-10 text-phosphor-ivory md:text-3xl md:leading-[1.2]">
                {
                  'For the moment your AI-built app gets 80% of the way there, then turns into a locked room with no windows.'
                }
              </p>
              <p className="mt-6 max-w-3xl text-base leading-8 text-white/62 md:text-lg">
                {
                  'You are staring at a chatbot that almost fixes the bug, breaks two other things, and cannot explain the code it just created. I turn that almost-product into software you can ship.'
                }
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" variant="primary">
                  <Link href={routePaths.contact}>{'Contact me'}</Link>
                </Button>
              </div>
            </div>

            <ServiceDreadPanel
              footer={'This is the part where vibe turns into engineering, and guessing gets expensive.'}
              signals={[
                {
                  label: 'demo',
                  value: 'Impressive enough to show people. Fragile enough to fear touching it.',
                },
                {
                  label: 'code',
                  value: 'You can paste it into chat, but you cannot tell what it means.',
                },
                {
                  label: 'loop',
                  value: 'One prompt fixes the button and quietly breaks auth.',
                },
                {
                  label: 'last mile',
                  value: 'The product is visible. The finish line keeps moving.',
                },
              ]}
              status={'80% IS NOT DONE'}
              title={'Last-mile pain'}
            />
          </div>
        </header>

        <section className="mt-10 grid gap-5 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="contact-panel p-6">
            <h2 className="text-base font-semibold text-phosphor-ivory">{'What this is good for'}</h2>
            <ul className="mt-4 grid gap-3 text-sm leading-7 text-white/68 md:grid-cols-3">
              {[
                'Turning 80% vibecoded demos into shippable product work.',
                'Explaining what the code is doing in normal language before changing it.',
                'Replacing chatbot guesswork with actual engineering judgment.',
              ].map((item) => (
                <li key={item} className="border-l border-white/12 pl-4">
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="contact-panel p-6">
            <h2 className="text-sm font-semibold text-primary">{'Engagement shapes'}</h2>
            <div className="mt-4 grid gap-2">
              {['Project builds', 'Short audits', 'Workshop sessions', 'Fractional advisory'].map((item) => (
                <p key={item} className="border-l border-white/12 pl-3 text-sm text-white/62">
                  {item}
                </p>
              ))}
            </div>
          </div>
        </section>

        <ServiceEngagementStrip
          engagements={[
            {
              description:
                'I read the repo and tell you in plain English what is real, fragile, fake, and worth fixing.',
              title: 'Audit',
            },
            {
              description: 'I take the broken last mile and turn it into working software with less lying in the code.',
              title: 'Fix',
            },
            {
              description: 'I show you how to use AI tools without letting the tools drive the product.',
              title: 'Teach',
            },
          ]}
        />

        <ol className="mt-10 grid gap-4 lg:grid-cols-3">
          {[
            {
              bestFor: 'You have the idea and the demo. Now it has to become software.',
              description:
                'Full-stack implementation for the app AI helped you start but cannot responsibly finish. Features, services, integrations, data flow, and the production details the bot keeps hand-waving.',
              outputs: ['Last-mile implementation', 'Production fixes', 'Maintainable code'],
              title: 'Software Engineering',
            },
            {
              bestFor: 'Your UI is one prompt away from breaking itself.',
              description:
                'React, TypeScript, forms, auth screens, state cleanup, SSR, and UI behavior for interfaces that looked fine until you clicked anything twice.',
              outputs: ['Stable UI', 'State cleanup', 'SSR-friendly pages'],
              title: 'Frontend Engineering',
            },
            {
              bestFor: 'You are tired of not knowing whether the app is close or cooked.',
              description:
                'A blunt read on what you actually have, what is fake, what is dangerous, and what needs to happen next before you spend another week arguing with an autocomplete box.',
              outputs: ['Plain-English diagnosis', 'Scope cuts', 'Build plan'],
              title: 'Consulting',
            },
            {
              bestFor: 'The demo worked. The last mile did not.',
              description:
                'Triage for the 80% app stuck in the endless almost-fixed loop: duplicated state, haunted workflows, mystery files, broken assumptions, and code nobody on the call can explain.',
              outputs: ['Damage report', 'Priority fixes', 'Cleaner code'],
              title: 'VibeCode Unfucking',
            },
            {
              bestFor: 'You want leverage without making the chatbot your CTO.',
              description:
                'Practical sessions for founders, operators, and teams using AI to build without blindly accepting code they cannot read, test, or recover from.',
              outputs: ['AI workflow', 'Prompt discipline', 'Review habits'],
              title: 'Effective AI Use Workshops',
            },
            {
              bestFor: 'You are past the toy phase and the weird failures are getting expensive.',
              description:
                'LLM workflows, RAG, MCP, model routing, agent runtimes, and tool-use systems for when the AI feature itself needs to behave like software, not a haunted text box.',
              outputs: ['Agent flows', 'Tool contracts', 'Evaluation loops'],
              title: 'AI Systems and Agent Tooling',
            },
            {
              bestFor: 'You do not know what good code looks like, but you can feel this is not it.',
              description:
                'A direct pass over the folder structure, data flow, dependencies, failure modes, and places where the AI invented structure that only looks like engineering.',
              outputs: ['System notes', 'Failure modes', 'Refactor sequence'],
              title: 'Architecture Review',
            },
            {
              bestFor: 'The app works on your machine because your machine is being polite.',
              description:
                'Slow pages, broken hydration, weird routes, cache mistakes, SEO-critical rendering, and the classic problem where localhost keeps lying to you.',
              outputs: ['Render fixes', 'Load improvements', 'Deployment checks'],
              title: 'Performance and SSR Repair',
            },
            {
              bestFor: 'You need engineering judgment before the app becomes folklore.',
              description:
                'Staff-level execution help for founders and teams who need someone to translate the mess, make decisions, review code, and keep the build from dissolving into prompt archaeology.',
              outputs: ['Review standards', 'Delivery rhythm', 'Sharper ownership'],
              title: 'Technical Leadership',
            },
          ].map((service, index) => (
            <li key={service.title}>
              <ServiceCard {...service} icon={serviceIcons[index] ?? Search} index={index} />
            </li>
          ))}
        </ol>

        <section className="mt-10 grid gap-5 rounded-lg border border-white/10 bg-card/80 p-6 md:grid-cols-3 md:items-center">
          <div className="md:col-span-2">
            <h2 className="text-xl font-semibold text-phosphor-ivory">{'Not sure which one fits?'}</h2>
            <p className="mt-3 text-sm leading-7 text-white/64">
              {
                'Send the half-working thing. If it is 80% real and 20% impossible to finish, that is exactly the part I can help with.'
              }
            </p>
          </div>
          <Button asChild className="justify-center" size="lg" variant="primary">
            <Link href={routePaths.contact}>{'Contact me'}</Link>
          </Button>
        </section>
      </main>
    </div>
  )
}
